import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrgCurrency } from "@/lib/currency-context";
import {
  ShoppingCart, Package, BarChart3, Store, RotateCcw,
  Plus, Trash2, Edit, Search, TrendingUp, AlertTriangle, Box
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

export function CommerceTab() {
  const { formatMoney } = useOrgCurrency();
  const [subTab, setSubTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [returns, setReturns] = useState([]);
  const [channels, setChannels] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [searchQ, setSearchQ] = useState("");

  const headers = useCallback(() => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  }, []);

  const fetchDashboard = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/commerce/dashboard`, { headers: headers() }); setDashboard(r.data); } catch (e) { console.error(e); }
  }, [headers]);

  const fetchOrders = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/commerce/orders`, { headers: headers(), params: { q: searchQ } }); setOrders(r.data || []); } catch (e) { console.error(e); }
  }, [headers, searchQ]);

  const fetchProducts = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/commerce/products`, { headers: headers(), params: { q: searchQ } }); setProducts(r.data || []); } catch (e) { console.error(e); }
  }, [headers, searchQ]);

  const fetchReturns = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/commerce/returns`, { headers: headers() }); setReturns(r.data || []); } catch (e) { console.error(e); }
  }, [headers]);

  const fetchChannels = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/commerce/channels`, { headers: headers() }); setChannels(r.data || []); } catch (e) { console.error(e); }
  }, [headers]);

  const fetchInventory = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/commerce/inventory`, { headers: headers() }); setInventory(r.data); } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    if (subTab === "orders") fetchOrders();
    if (subTab === "products") fetchProducts();
    if (subTab === "returns") fetchReturns();
    if (subTab === "channels") fetchChannels();
    if (subTab === "inventory") fetchInventory();
  }, [subTab, fetchOrders, fetchProducts, fetchReturns, fetchChannels, fetchInventory]);

  // --- Dashboard ---
  const DashboardView = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Commerce Studio</h2>
      {dashboard ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4"><div className="text-sm text-gray-500">Umsatz (Monat)</div><div className="text-2xl font-bold">{formatMoney(dashboard.umsatz_gesamt)}</div></Card>
            <Card className="p-4"><div className="text-sm text-gray-500">Bestellungen</div><div className="text-2xl font-bold">{dashboard.bestellungen_gesamt || 0}</div></Card>
            <Card className="p-4"><div className="text-sm text-gray-500">Durchschn. Bestellwert</div><div className="text-2xl font-bold">{formatMoney(dashboard.durchschnittlicher_bestellwert)}</div></Card>
            <Card className="p-4"><div className="text-sm text-gray-500">Offene Bestellungen</div><div className="text-2xl font-bold text-orange-500">{dashboard.bestellungen_offen || 0}</div></Card>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Umsatz nach Kanal</h3>
              {Object.keys(dashboard.umsatz_nach_kanal || {}).length === 0 ? <p className="text-sm text-gray-400">Keine Daten</p> : (
                <div className="space-y-1">{Object.entries(dashboard.umsatz_nach_kanal).map(([ch, amt]) => (
                  <div key={ch} className="flex justify-between text-sm"><span className="capitalize">{ch.replace(/_/g, " ")}</span><span className="font-medium">{formatMoney(amt)}</span></div>
                ))}</div>
              )}
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Top Produkte</h3>
              {(dashboard.top_produkte || []).length === 0 ? <p className="text-sm text-gray-400">Keine Daten</p> : (
                <div className="space-y-1">{dashboard.top_produkte.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex justify-between text-sm"><span>{p.name}</span><span className="font-medium">{p.menge}x verkauft</span></div>
                ))}</div>
              )}
            </Card>
          </div>
        </>
      ) : <p className="text-gray-400">Lade Dashboard...</p>}
    </div>
  );

  // --- Orders ---
  const OrdersView = () => {
    const [form, setForm] = useState({ kunde_name: "", kunde_email: "", kanal: "eigener_shop", betrag: "", versandkosten: "0", status: "neu", zahlungsstatus: "offen", notizen: "" });
    const save = async () => {
      try {
        const data = { ...form, betrag: parseFloat(form.betrag) || 0, versandkosten: parseFloat(form.versandkosten) || 0 };
        if (editItem) { await axios.put(`${API}/api/commerce/orders/${editItem.id}`, data, { headers: headers() }); toast.success("Aktualisiert"); }
        else { await axios.post(`${API}/api/commerce/orders`, data, { headers: headers() }); toast.success("Bestellung angelegt"); }
        setShowForm(false); setEditItem(null); fetchOrders();
      } catch (e) { toast.error("Fehler"); }
    };
    const statusColor = (s) => ({ neu: "text-blue-600 bg-blue-50", bezahlt: "text-green-600 bg-green-50", versendet: "text-purple-600 bg-purple-50", abgeschlossen: "text-gray-600 bg-gray-100", storniert: "text-red-600 bg-red-50", retoure: "text-orange-600 bg-orange-50" }[s] || "text-gray-600 bg-gray-50");

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Bestellungen</h2>
          <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ kunde_name: "", kunde_email: "", kanal: "eigener_shop", betrag: "", versandkosten: "0", status: "neu", zahlungsstatus: "offen", notizen: "" }); }} className="gap-1"><Plus className="w-4 h-4" /> Neue Bestellung</Button>
        </div>
        <Input placeholder="Suchen..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="max-w-xs" />
        {showForm && (
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Kunde</Label><Input value={form.kunde_name} onChange={(e) => setForm({...form, kunde_name: e.target.value})} /></div>
              <div><Label>E-Mail</Label><Input value={form.kunde_email} onChange={(e) => setForm({...form, kunde_email: e.target.value})} /></div>
              <div><Label>Kanal</Label>
                <Select value={form.kanal} onValueChange={(v) => setForm({...form, kanal: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eigener_shop">Eigener Shop</SelectItem>
                    <SelectItem value="shopify">Shopify</SelectItem>
                    <SelectItem value="woocommerce">WooCommerce</SelectItem>
                    <SelectItem value="etsy">Etsy</SelectItem>
                    <SelectItem value="ebay">eBay</SelectItem>
                    <SelectItem value="amazon">Amazon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Betrag</Label><Input type="number" step="0.01" value={form.betrag} onChange={(e) => setForm({...form, betrag: e.target.value})} /></div>
              <div><Label>Versandkosten</Label><Input type="number" step="0.01" value={form.versandkosten} onChange={(e) => setForm({...form, versandkosten: e.target.value})} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neu">Neu</SelectItem>
                    <SelectItem value="bezahlt">Bezahlt</SelectItem>
                    <SelectItem value="in_bearbeitung">In Bearbeitung</SelectItem>
                    <SelectItem value="versendet">Versendet</SelectItem>
                    <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                    <SelectItem value="storniert">Storniert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2"><Button onClick={save}>Speichern</Button><Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button></div>
          </Card>
        )}
        <div className="space-y-2">
          {orders.length === 0 ? <p className="text-gray-400 text-sm">Keine Bestellungen</p> : orders.map((o) => (
            <Card key={o.id} className="p-3 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium">{o.bestellnummer || o.id} – {o.kunde_name || "Unbekannt"}</div>
                <div className="text-sm text-gray-500">{o.datum} | {o.kanal?.replace(/_/g, " ")}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded ${statusColor(o.status)}`}>{o.status}</span>
                <span className="font-bold">{formatMoney(o.betrag, o.currency)}</span>
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(o); setForm(o); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // --- Products ---
  const ProductsView = () => {
    const [form, setForm] = useState({ name: "", sku: "", kategorie: "", einkaufspreis: "", verkaufspreis: "", bestand: "0", mindestbestand: "0" });
    const save = async () => {
      try {
        const data = { ...form, einkaufspreis: parseFloat(form.einkaufspreis) || 0, verkaufspreis: parseFloat(form.verkaufspreis) || 0, bestand: parseInt(form.bestand) || 0, mindestbestand: parseInt(form.mindestbestand) || 0 };
        if (editItem) { await axios.put(`${API}/api/commerce/products/${editItem.id}`, data, { headers: headers() }); toast.success("Aktualisiert"); }
        else { await axios.post(`${API}/api/commerce/products`, data, { headers: headers() }); toast.success("Produkt angelegt"); }
        setShowForm(false); setEditItem(null); fetchProducts();
      } catch (e) { toast.error("Fehler"); }
    };

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Produkte</h2>
          <Button onClick={() => { setShowForm(true); setEditItem(null); setForm({ name: "", sku: "", kategorie: "", einkaufspreis: "", verkaufspreis: "", bestand: "0", mindestbestand: "0" }); }} className="gap-1"><Plus className="w-4 h-4" /> Neues Produkt</Button>
        </div>
        <Input placeholder="Suchen..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="max-w-xs" />
        {showForm && (
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div><Label>SKU / Artikelnr.</Label><Input value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} /></div>
              <div><Label>Kategorie</Label><Input value={form.kategorie} onChange={(e) => setForm({...form, kategorie: e.target.value})} /></div>
              <div><Label>Einkaufspreis</Label><Input type="number" step="0.01" value={form.einkaufspreis} onChange={(e) => setForm({...form, einkaufspreis: e.target.value})} /></div>
              <div><Label>Verkaufspreis</Label><Input type="number" step="0.01" value={form.verkaufspreis} onChange={(e) => setForm({...form, verkaufspreis: e.target.value})} /></div>
              <div><Label>Bestand</Label><Input type="number" value={form.bestand} onChange={(e) => setForm({...form, bestand: e.target.value})} /></div>
              <div><Label>Mindestbestand</Label><Input type="number" value={form.mindestbestand} onChange={(e) => setForm({...form, mindestbestand: e.target.value})} /></div>
            </div>
            <div className="flex gap-2"><Button onClick={save}>Speichern</Button><Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button></div>
          </Card>
        )}
        <div className="space-y-2">
          {products.length === 0 ? <p className="text-gray-400 text-sm">Keine Produkte</p> : products.map((p) => (
            <Card key={p.id} className="p-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-sm text-gray-500">{p.sku && `SKU: ${p.sku} | `}Bestand: {p.bestand || 0}</div>
              </div>
              <div className="flex items-center gap-3">
                {(p.bestand || 0) <= (p.mindestbestand || 0) && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                <span className="font-bold">{formatMoney(p.verkaufspreis, p.currency)}</span>
                <Button size="sm" variant="ghost" onClick={() => { setEditItem(p); setForm({...p, einkaufspreis: String(p.einkaufspreis||""), verkaufspreis: String(p.verkaufspreis||""), bestand: String(p.bestand||0), mindestbestand: String(p.mindestbestand||0)}); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/commerce/products/${p.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchProducts(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // --- Inventory ---
  const InventoryView = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Bestandsübersicht</h2>
      {inventory ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Card className="p-4"><div className="text-sm text-gray-500">Produkte gesamt</div><div className="text-2xl font-bold">{inventory.produkte_gesamt}</div></Card>
            <Card className="p-4"><div className="text-sm text-gray-500">Aktive Produkte</div><div className="text-2xl font-bold text-green-600">{inventory.produkte_aktiv}</div></Card>
            <Card className="p-4"><div className="text-sm text-gray-500">Nicht verfügbar</div><div className="text-2xl font-bold text-red-600">{(inventory.nicht_verfuegbar || []).length}</div></Card>
          </div>
          {(inventory.niedriger_bestand || []).length > 0 && (
            <Card className="p-4">
              <h3 className="font-semibold mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-orange-500" /> Niedriger Bestand</h3>
              <div className="space-y-1">{inventory.niedriger_bestand.map((p) => (
                <div key={p.id} className="flex justify-between text-sm p-2 bg-orange-50 rounded">
                  <span>{p.name}</span>
                  <span className="font-medium">{p.bestand} / {p.mindestbestand} (Mindest)</span>
                </div>
              ))}</div>
            </Card>
          )}
        </>
      ) : <p className="text-gray-400">Lade Bestand...</p>}
    </div>
  );

  // --- Returns ---
  const ReturnsView = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Retouren & Erstattungen</h2>
      <div className="space-y-2">
        {returns.length === 0 ? <p className="text-gray-400 text-sm">Keine Retouren</p> : returns.map((r) => (
          <Card key={r.id} className="p-3 flex items-center justify-between">
            <div>
              <div className="font-medium">Bestellung: {r.bestellung_id}</div>
              <div className="text-sm text-gray-500">{r.grund} | {r.created_at?.slice(0,10)}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs px-2 py-0.5 rounded ${r.status === "erstattet" ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>{r.status}</span>
              <span className="font-bold">{formatMoney(r.betrag, r.currency)}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // --- Channels ---
  const ChannelsView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Verkaufskanäle</h2>
        <Button onClick={async () => {
          const name = prompt("Kanalname:");
          if (!name) return;
          await axios.post(`${API}/api/commerce/channels`, { name, typ: "sonstige", aktiv: true }, { headers: headers() });
          toast.success("Kanal angelegt"); fetchChannels();
        }} className="gap-1"><Plus className="w-4 h-4" /> Neuer Kanal</Button>
      </div>
      <div className="space-y-2">
        {channels.length === 0 ? <p className="text-gray-400 text-sm">Keine Verkaufskanäle konfiguriert</p> : channels.map((ch) => (
          <Card key={ch.id} className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Store className="w-4 h-4 text-gray-400" />
              <div>
                <div className="font-medium">{ch.name}</div>
                <div className="text-xs text-gray-500">{ch.typ} | {ch.aktiv ? "Aktiv" : "Inaktiv"}</div>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={async () => { await axios.delete(`${API}/api/commerce/channels/${ch.id}`, { headers: headers() }); toast.success("Gelöscht"); fetchChannels(); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
          </Card>
        ))}
      </div>
    </div>
  );

  // --- Main ---
  const subTabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "orders", label: "Bestellungen", icon: ShoppingCart },
    { id: "products", label: "Produkte", icon: Package },
    { id: "inventory", label: "Bestand", icon: Box },
    { id: "channels", label: "Kanäle", icon: Store },
    { id: "returns", label: "Retouren", icon: RotateCcw },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-2 border-b">
        {subTabs.map((t) => (
          <button key={t.id} onClick={() => { setSubTab(t.id); setShowForm(false); setEditItem(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${subTab === t.id ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>
      {subTab === "dashboard" && <DashboardView />}
      {subTab === "orders" && <OrdersView />}
      {subTab === "products" && <ProductsView />}
      {subTab === "inventory" && <InventoryView />}
      {subTab === "channels" && <ChannelsView />}
      {subTab === "returns" && <ReturnsView />}
    </div>
  );
}
